import { getMainSiteUrl } from "../constants/appConfig";
import { SITE } from "../constants/site";
import { productDetailPath } from "../constants/routes";
import { formatPrice, getProductUnitDisplay } from "./mapWooProduct";
import { getDisplayCategory, getStockStatusLabel } from "./productDisplay";
import { getColorLabel } from "./productColors";

export function buildWhatsAppUrl(message) {
  const phone = SITE.whatsappNumber.replace(/\D/g, "");
  const text = encodeURIComponent(message);
  return `https://wa.me/${phone}?text=${text}`;
}

function appendLine(lines, label, value) {
  const normalized = value === null || value === undefined ? "" : String(value).trim();
  if (!normalized) return;
  lines.push(`• ${label}: ${normalized}`);
}

function buildProductUrl(slug, origin) {
  if (!slug) return "";
  const base = (origin || getMainSiteUrl()).replace(/\/+$/, "");
  return `${base}${productDetailPath(slug)}`;
}

export function buildProductQuoteMessage(product, quantity = 1, { t, origin, language } = {}) {
  const translate = (key, fallback) => (t ? t(key) : fallback);

  if (!product?.name) {
    return translate("whatsapp.quote.default", "Hello CAF Sports, I would like to request a quote.");
  }

  const category = getDisplayCategory(product.categories);
  const unitDisplay = getProductUnitDisplay(product);
  const productOrigin = origin || product.origin;
  const lines = [
    translate("whatsapp.quote.greeting", "Hello CAF Sports,"),
    "",
    translate("whatsapp.quote.intro", "I need this product:"),
    "",
  ];

  appendLine(lines, translate("whatsapp.quote.product", "Product"), product.name);
  appendLine(lines, translate("whatsapp.quote.category", "Category"), category?.name);
  appendLine(
    lines,
    translate("Color", "Color"),
    product.selectedColor || product.color
      ? getColorLabel(product.selectedColor || product.color, language)
      : "",
  );
  appendLine(lines, translate("whatsapp.quote.price", "Price"), product.formattedPrice);
  appendLine(lines, translate("whatsapp.quote.pack", "Pack"), unitDisplay);
  appendLine(lines, translate("whatsapp.quote.unit", "Unit"), product.unit);
  appendLine(
    lines,
    translate("whatsapp.quote.unitWeight", "Unit weight"),
    product.unitWeight,
  );
  appendLine(lines, translate("whatsapp.quote.quantity", "Quantity"), quantity);
  appendLine(lines, translate("whatsapp.quote.sku", "SKU"), product.sku);
  appendLine(
    lines,
    translate("whatsapp.quote.availability", "Availability"),
    product.stockStatus && t
      ? getStockStatusLabel(product.stockStatus, t)
      : product.inStock === false
        ? translate("Out of stock", "Out of stock")
        : product.inStock
          ? translate("In stock", "In stock")
          : "",
  );
  appendLine(
    lines,
    translate("whatsapp.quote.link", "Product link"),
    buildProductUrl(product.slug, productOrigin),
  );

  lines.push("", translate("whatsapp.quote.thanks", "Thank you."));
  return lines.join("\n");
}

export function buildCartQuoteMessage(items, { t, subtotal, language } = {}) {
  const translate = (key, fallback) => (t ? t(key) : fallback);

  if (!items?.length) {
    return translate("whatsapp.quote.default", "Hello CAF Sports, I would like to request a quote.");
  }

  const lines = [
    translate("whatsapp.quote.greeting", "Hello CAF Sports,"),
    "",
    translate("whatsapp.quote.cartIntro", "I would like a quote for the following items:"),
    "",
  ];

  items.forEach((item, index) => {
    lines.push(`${index + 1}. ${item.name}`);
    appendLine(lines, translate("whatsapp.quote.category", "Category"), item.categoryName);
    appendLine(
      lines,
      translate("Color", "Color"),
      item.color ? getColorLabel(item.color, language) : "",
    );
    appendLine(
      lines,
      translate("whatsapp.quote.price", "Price"),
      item.formattedPrice || formatPrice(String(item.price)),
    );
    appendLine(lines, translate("whatsapp.quote.pack", "Pack"), item.unitLabel);
    appendLine(lines, translate("whatsapp.quote.unit", "Unit"), item.unit);
    appendLine(lines, translate("whatsapp.quote.unitWeight", "Unit weight"), item.unitWeight);
    appendLine(lines, translate("whatsapp.quote.quantity", "Quantity"), item.quantity);
    appendLine(lines, translate("whatsapp.quote.sku", "SKU"), item.sku);
    appendLine(
      lines,
      translate("whatsapp.quote.link", "Product link"),
      buildProductUrl(item.slug, item.origin),
    );
    if (index < items.length - 1) lines.push("");
  });

  if (subtotal != null && !Number.isNaN(subtotal)) {
    lines.push("");
    lines.push(
      `• ${translate("whatsapp.quote.subtotal", "Subtotal")}: ${formatPrice(String(subtotal))}`,
    );
  }

  lines.push("", translate("whatsapp.quote.thanks", "Thank you."));
  return lines.join("\n");
}

/** @deprecated Use buildProductQuoteMessage with a product object */
export function buildProductQuoteMessageLegacy(productName) {
  return `I am interested in ${productName}`;
}